/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { BarChart } from "../models/http/charts/bar-chart/bar-chart.model";
import { MixedChart } from "../models/http/charts/mixed-chart/mixed-chart.model";
import { PieChart } from "../models/http/charts/pie-chart/pie-chart.model";
import { HardwareAssetsRealtimeInformation } from "../models/http/dashboard/hardware-assets-realtime-information.model";
import { CioMode } from "../models/ui/cio-mode.model";
import { DashboardCioHttpService } from "./http/dashboard-cio-http.service";
import { ChartResponse } from "../models/ui/charts.model";

@Injectable({
	providedIn: "root"
})
export class DashboardCioService {
	constructor(private _dashboardCioHttpService: DashboardCioHttpService) {}

	/**
	 * Retrieves the summary for the specified time range.
	 *
	 * @param startDate - The start date for the time range.
	 * @param endDate - The end date for the time range.
	 * @returns An observable containing the summary data.
	 */
	public getSummary(
		startDate: string,
		endDate?: string
	): Observable<HardwareAssetsRealtimeInformation> {
		return this._dashboardCioHttpService.getSummary(startDate, endDate);
	}

	/**
	 * Retrieves the forecast data for the specified time range and mode.
	 *
	 * @param startDate - The start date for the time range.
	 * @param endDate - The end date for the time range.
	 * @param mode - The mode in which the forecast data should be retrieved.
	 * @returns An Observable that emits the forecast data in the form of a MixedChart.
	 */
	public getForecast(
		startDate: string,
		endDate: string | undefined,
		mode: CioMode
	): Observable<ChartResponse<MixedChart>> {
		return this._dashboardCioHttpService.getForecast(startDate, endDate, mode);
	}

	/**
	 * Retrieves the emissions share data for the specified time range and mode.
	 *
	 * @param startDate - The start date for the time range.
	 * @param endDate - The end date for the time range.
	 * @param mode - The mode in which to retrieve emissions share data.
	 * @returns An Observable that emits the PieChart data representing the emissions share.
	 */
	public getEmissionsShare(
		startDate: string,
		endDate: string | undefined,
		mode: CioMode
	): Observable<ChartResponse<PieChart>> {
		return this._dashboardCioHttpService.getEmissionsShare(startDate, endDate, mode);
	}

	/**
	 * Retrieves the details for the specified time range and scope.
	 *
	 * @param startDate - The start date for the time range.
	 * @param endDate - The end date for the time range.
	 * @param scope - The scope to retrieve the details for.
	 * @returns An Observable that emits a BarChart object.
	 */
	public getDetails(
		startDate: string,
		endDate: string | undefined,
		scope: string
	): Observable<BarChart> {
		return this._dashboardCioHttpService.getDetails(startDate, endDate, scope);
	}
}
